import { RotateCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { WebhookEventStatus } from '@/lib/api/schemas/webhookEventStatus';

export interface WebhookFilters {
  status?: WebhookEventStatus;
  clientId?: string;
  merchantId?: string;
  eventCode?: string;
  pspReference?: string;
  from?: string;
  to?: string;
}

interface Props {
  pending: WebhookFilters;
  onChange: (next: WebhookFilters) => void;
  onApply: () => void;
  onReset: () => void;
  /** Hide the client filter — used by the partner portal where client is implicit. */
  hideClient?: boolean;
  /** Total result count to render on the right side of the action row. */
  resultCount?: number | string;
}

/**
 * Shared filter strip used by both the admin Webhooks page and the partner
 * portal Webhooks page. The portal hides the Client filter.
 */
export function WebhookFiltersBar({
  pending,
  onChange,
  onApply,
  onReset,
  hideClient,
  resultCount,
}: Props) {
  const set = (patch: Partial<WebhookFilters>) => onChange({ ...pending, ...patch });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Filters</CardTitle>
        <CardDescription>Press Apply to refresh — all filters combine with AND.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <FilterField label="Status">
          <Select
            value={pending.status ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              set({ status: v ? (v as WebhookEventStatus) : undefined });
            }}
          >
            <option value="">Any</option>
            <option value={WebhookEventStatus.Received}>Received</option>
            <option value={WebhookEventStatus.Dispatching}>Dispatching</option>
            <option value={WebhookEventStatus.Delivered}>Delivered</option>
            <option value={WebhookEventStatus.Failed}>Failed</option>
            <option value={WebhookEventStatus.Abandoned}>Abandoned</option>
          </Select>
        </FilterField>

        <FilterField label="Event code">
          <Input
            placeholder="AUTHORISATION"
            value={pending.eventCode ?? ''}
            onChange={(e) => set({ eventCode: e.target.value })}
          />
        </FilterField>

        <FilterField label="PSP reference">
          <Input
            placeholder="881234567890ABCD"
            value={pending.pspReference ?? ''}
            onChange={(e) => set({ pspReference: e.target.value })}
          />
        </FilterField>

        {!hideClient && (
          <FilterField label="Client ID">
            <Input
              placeholder="00000000-0000-0000-0000-000000000000"
              value={pending.clientId ?? ''}
              onChange={(e) => set({ clientId: e.target.value })}
            />
          </FilterField>
        )}

        <FilterField label="Merchant ID">
          <Input
            placeholder="00000000-0000-0000-0000-000000000000"
            value={pending.merchantId ?? ''}
            onChange={(e) => set({ merchantId: e.target.value })}
          />
        </FilterField>

        <FilterField label="From">
          <Input
            type="datetime-local"
            value={pending.from ?? ''}
            onChange={(e) => set({ from: e.target.value })}
          />
        </FilterField>

        <FilterField label="To">
          <Input
            type="datetime-local"
            value={pending.to ?? ''}
            onChange={(e) => set({ to: e.target.value })}
          />
        </FilterField>

        <div className="flex items-end gap-2 md:col-span-2 xl:col-span-3">
          <Button onClick={onApply} className="gap-2">
            <RotateCw className="h-4 w-4" />
            Apply
          </Button>
          <Button variant="ghost" onClick={onReset}>
            Reset
          </Button>
          <div className="ml-auto text-xs text-muted-foreground">
            {resultCount !== undefined ? `${resultCount} matches` : ' '}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
